
import { defineComponent } from "vue";
import {
  useAutoaddress,
  useCognitoAdmin,
  useGammaData,
  useLoading,
  useLocation,
  useMap,
  useOrgSettings,
  useDataHitcodes,
} from "@/services";
import { mapState } from "pinia";
import { isEqual } from "lodash-es";
import { ILocation, LatLng } from "@/types";
import Spinner from "@/components/shared/Spinner.vue";
import CoordinatesInput from "@/components/shared/CoordinatesInput.vue";
import Autoaddress from "@autoaddress.ie/autoaddress-vue/src/Autoaddress.vue";
import { FindAddressResponse } from "@/types/autoaddress";
import { Point } from "geojson";
import GDropdown from "@/components/shared/GDropdown.vue";
import { useAppConfig } from "@/config";
import { HIT_CODES, HitCode } from "@/services/dataHitCodes";
export default defineComponent({
  name: "CoordinateEditor",
  components: { GDropdown, Spinner, CoordinatesInput, Autoaddress },
  computed: {
    ...mapState(useMap, ["markerPosition"]),
    ...mapState(useLocation, ["locationType"]),

    licenseKey(): string | undefined {
      return useOrgSettings().autoAddressProvider?.licenceKey;
    },
    coords: {
      get(): LatLng {
        return {
          lng: useMap().markerPosition?.lng || useLocation().coordinates[0],
          lat: useMap().markerPosition?.lat || useLocation().coordinates[1],
        };
      },
      set(val: LatLng) {
        const mapStore = useMap();
        if (mapStore.markerPosition) {
          mapStore.markerPosition.lng = val.lng || 0;
          mapStore.markerPosition.lat = val.lat || 0;
        } else {
          useLocation().coordinates[0] = val.lng || 0;
          useLocation().coordinates[1] = val.lat || 0;
        }
      },
    },
    hitCodeList(): { label: string; value: number }[] {
      return (
        HIT_CODES.map((hit) => ({
          label: this.getTextForHitcode(hit.hitcode),
          value: hit.hitcode,
        })) || []
      );
    },
    hasPermission(): boolean {
      return useCognitoAdmin().currentUserGroups.some((g) =>
        useAppConfig().adminRoles.includes(g)
      );
    },
    hasHitCode(): boolean {
      const columns = useLocation()?.location?._data?.record?.columnDefinitions;
      return ["HIT_CODE"].every((col) =>
        columns?.some((c) => c.columnName === col)
      );
    },
    selectedHitCode(): HitCode | null | undefined {
      return useDataHitcodes().selectedHitCode;
    },
    isEditingCoordinates: {
      get(): boolean {
        return useMap().isEditingCoordinates;
      },
      set(val: boolean) {
        useMap().isEditingCoordinates = val;
      },
    },
    currentLocation(): any {
      return useLocation().coordinates;
    },
    isApplyDisabled(): boolean {
      return (
        isEqual(this.currentLocation, [this.coords.lng, this.coords.lat]) &&
        this.recordHitCode === this.selectedHitCode?.hitcode
      );
    },
    value(): LatLng {
      return {
        lng: useMap().markerPosition?.lng || this.currentLocation[0],
        lat: useMap().markerPosition?.lat || this.currentLocation[1],
      };
    },
    coordinateLocation(): ILocation {
      return {
        type: "Coordinate",
        geometry: {
          type: "Point",
          coordinates: [this.coords.lng || 0, this.coords.lat || 0],
        },
      };
    },
    isUpdatingRecord(): boolean {
      return useLoading().isLoadingData("record-update");
    },
    recordHitCode(): number {
      const record = useLocation()?.location?._data?.record;
      return (
        (record?.data?.[
          record.columnDefinitions?.findIndex(
            (col) => col.columnName === "HIT_CODE_NUM"
          )
        ] as number) ?? undefined
      );
    },
  },
  watch: {
    isEditingCoordinates: {
      handler(val) {
        if (val) {
          useDataHitcodes().setSelected(this.recordHitCode);
          this.coords = this.value;
        } else {
          useDataHitcodes().setSelected(undefined);
        }
      },
      immediate: true,
    },
    markerPosition: {
      handler(val, oldVal) {
        if (val) {
          const applyDef =
            !oldVal || val.lat !== oldVal.lat || val.lon !== oldVal.lon;
          if (applyDef && this.isEditingCoordinates)
            this.defaultUpdateHitcode();
          Object.assign(this, val);
        }
      },
    },
  },
  methods: {
    close() {
      this.isEditingCoordinates = false;
    },
    aaInit() {
      (this.$refs.autoaddress as any).$el
        .getElementsByTagName("input")[0]
        .setAttribute("autocomplete", "none");
    },
    async addressFound(result: FindAddressResponse) {
      const ecadData = await useAutoaddress().getLatLng({
        ecadid: result.addressId,
        postcode: result.postcode || "",
        country: "ie",
      });
      const { latitude, longitude } = ecadData.spatialInfo.etrs89.location;

      this.coords = {
        lat: latitude,
        lng: longitude,
      };
    },
    updateCoords() {
      if (!this.isApplyDisabled) {
        if (
          !isEqual(this.currentLocation, [this.coords.lng, this.coords.lat])
        ) {
          if (this.locationType === "Coordinate") {
            useLocation().updateLocation(this.coordinateLocation);
          } else if (this.locationType !== "Address") {
            if (this.coordinateLocation.geometry) {
              const updateRequest = useLocation().record?.updateGeometryQuery(
                this.coordinateLocation.geometry as Point
              );
              if (updateRequest) {
                useGammaData().updateDatasetRecord(
                  updateRequest,
                  useLocation().record?.lcId || ""
                );
              }
            }
          }
        }
        if (
          this.selectedHitCode?.hitcode &&
          this.recordHitCode !== this.selectedHitCode?.hitcode
        ) {
          const updateRequestNum = useLocation().record?.updateHitcodeNumQuery(
            this.selectedHitCode?.hitcode
          );

          if (updateRequestNum) {
            useGammaData().updateDatasetRecord(
              updateRequestNum,
              useLocation().record?.lcId || ""
            );
          }
        }
      }
    },
    hitCodeSelected(item: number) {
      useDataHitcodes().setSelected(item);
    },
    defaultUpdateHitcode() {
      if (this.isEditingCoordinates) this.hitCodeSelected(-200);
    },
    getTextForHitcode(hitcode: number) {
      const hit = HIT_CODES.find((h) => h.hitcode === hitcode);
      return hit
        ? `${hit.shortCode} ${hit.hitcode + ""}  -  ${hit.description}`
        : "Unavaliable hit code";
    },
  },
});
